<template>
  <div class="newton-second-law-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F = m · a$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div ref="canvasContainer" class="simulation-stage">
        <div class="hud-panel">
          <div class="hud-item blue">
             <label>速度 (v)</label>
             <span class="val">{{ v.toFixed(2) }} <small>m/s</small></span>
          </div>
          <div class="hud-item green">
             <label>加速度 (a)</label>
             <span class="val">{{ acceleration.toFixed(2) }} <small>m/s²</small></span>
          </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
           <div class="control-item">
              <div class="label-row">
                 <span class="label">推力 (F)</span>
                 <span class="val">{{ force }} N</span>
              </div>
              <el-slider v-model="force" :min="0" :max="50" size="small"></el-slider>
           </div>
           <div class="control-item">
              <div class="label-row">
                 <span class="label">质量 (m)</span>
                 <span class="val">{{ mass }} kg</span>
              </div>
              <el-slider v-model="mass" :min="0.5" :max="10" :step="0.5" size="small"></el-slider>
           </div>
        </div>

        <div class="action-footer">
           <el-button type="primary" size="small" :disabled="isMoving" @click="startShift" icon="el-icon-video-play">开始施力</el-button>
           <el-button size="small" type="info" plain @click="reset" icon="el-icon-refresh-right">重置实验</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'NewtonSecondLawViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      force: 15,
      mass: 2,
      pos: 0,
      v: 0,
      isMoving: false,
      running: true
    }
  },
  computed: {
    acceleration() { return this.force / this.mass; }
  },
  mounted() { this.initThree(); },
  beforeDestroy() {
    this.running = false;
    if (this.frameId) cancelAnimationFrame(this.frameId);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
    this.scene = null;
    this.camera = null;
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf1f5f9);
      this.camera = new THREE.PerspectiveCamera(40, container.clientWidth / container.clientHeight, 1, 1000);
      this.camera.position.set(50, 40, 90);
      this.camera.lookAt(0, 5, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(this.renderer.domElement);

      this.scene.add(new THREE.GridHelper(400, 40, 0xe2e8f0, 0xf1f5f9)).position.y = 0;

      const blockGeo = new THREE.BoxGeometry(10, 10, 10);
      const blockMat = new THREE.MeshPhongMaterial({ color: 0x3b82f6, shininess: 30 });
      this.block = new THREE.Mesh(blockGeo, blockMat);
      this.block.position.y = 5;
      this.scene.add(this.block);

      this.forceArrow = new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), new THREE.Vector3(-15, 5, 0), 12, 0xef4444, 10, 4);
      this.scene.add(this.forceArrow);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.6));
      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(30, 80, 50);
      this.scene.add(light);

      this.animate();
    },
    startShift() { 
      this.reset(); // Crucial: Reset to initial state before starting new push
      this.isMoving = true; 
    },
    reset() { this.v = 0; this.pos = 0; this.block.position.x = 0; this.isMoving = false; },
    animate() {
       if (this.frameId) cancelAnimationFrame(this.frameId);
       if (!this.running) return;
       this.frameId = requestAnimationFrame(this.animate);
       if (this.isMoving) {
          const dt = 0.016;
          this.v += this.acceleration * dt;
          this.pos += this.v;
          if (this.block) {
            this.block.position.x = this.pos;
            this.forceArrow.position.x = this.pos - 15;
          }
          if (this.camera) {
            this.camera.position.x = this.pos + 50;
            this.camera.lookAt(this.pos, 5, 0);
          }
          if (this.pos > 300) {
             this.reset();
          }
       }
       // Ensure block is always visible if not moving but reset to a specific state
       if (!this.isMoving && this.pos === 0 && this.block) {
          this.block.position.set(0, 5, 0);
          this.forceArrow.position.set(-15, 5, 0);
          this.camera.position.set(50, 40, 90);
          this.camera.lookAt(0, 5, 0);
       }
       if (this.renderer && this.scene && this.camera) {
         this.renderer.render(this.scene, this.camera);
       }
    }
  }
}
</script>

<style scoped lang="scss">
.newton-second-law-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #ffffff; border-radius: 12px; border: 1.5px solid #e2e8f0; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: #ffffff; border-radius: 8px; border: 1px solid #e2e8f0; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #1e293b; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #64748b; font-weight: normal; } } &.blue { border-left: 3px solid #3b82f6; } &.green { border-left: 3px solid #10b981; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .control-item { .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } } } }
.action-footer { display: flex; gap: 8px; .el-button { flex: 1; font-weight: 700; } }
</style>
